/**
 * Where a winder's curated stock image lives in Storage.
 *
 * Its own module rather than a private helper in firebase.ts for the same
 * reason `winderDocId.ts` is: this prefix is a CROSS-REPO CONTRACT that three
 * separate systems have already been configured for, and none of them will
 * tell you at build time if it changes.
 *
 *   1. watchlock's `storage.rules` carries an `admin/winders/{winderId}/...`
 *      block (plus a `thumbnails/` sub-block, because Storage rules do NOT
 *      cascade into deeper path segments). A different prefix falls through to
 *      the default deny and every upload 403s.
 *   2. The storage-resize-images extension's `INCLUDE_PATH_LIST` is
 *      `/watchBrands,/admin,/calibres`. Being under `admin/` is what generates
 *      the 384x384 and 1024x1024 variants, with no extension config change.
 *   3. watchlock's `RESIZED_PATH_PREFIXES` (src/utils/imageUrlResolver.ts)
 *      mirrors that list, so the RN winder list can ask for a thumbnail
 *      instead of pulling the full image into a 56pt row.
 *
 * So the prefix is not a naming preference — it is the reason the feature works
 * without an extension redeploy, a rules rewrite, or a backfill.
 */

/** The Storage prefix every winder catalog image sits under. */
export const WINDER_IMAGE_PREFIX = 'admin/winders';

/** Fallback when a Blob arrives with no filename to take an extension from. */
const DEFAULT_EXTENSION = 'jpg';

/**
 * `admin/winders/{docId}/{timestamp}.{ext}`.
 *
 * TIMESTAMPED, never a fixed `{docId}.jpg`. These objects are served with long
 * cache lifetimes, so a stable path would pin the first image an admin ever
 * uploaded — a replacement would appear to do nothing, in the app and in this
 * editor alike. One upload, one path, forever; the caller deletes the object it
 * replaced once the document has stopped pointing at it.
 *
 * `timestamp` is injected rather than read from the clock here so the rule can
 * be tested without freezing time.
 */
export function buildWinderImagePath(
  winderDocId: string,
  fileName: string | undefined,
  timestamp: number,
): string {
  const docId = (winderDocId || '').trim();
  if (!docId) {
    throw new Error('Save the winder before adding an image');
  }
  return `${WINDER_IMAGE_PREFIX}/${docId}/${timestamp}.${extensionOf(fileName)}`;
}

/**
 * The lower-cased extension of a filename, or `jpg`.
 *
 * A name with no dot, a trailing dot, or a dot only in a directory segment all
 * yield the default rather than an empty or nonsense extension — the path is
 * matched against a content-type check in storage.rules, and a file called
 * `winder.` producing `…/1700.` would be rejected there with nothing on screen
 * explaining why.
 */
function extensionOf(fileName?: string): string {
  const name = (fileName || '').trim();
  const lastDot = name.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === name.length - 1) return DEFAULT_EXTENSION;
  const ext = name.slice(lastDot + 1).toLowerCase();
  return /^[a-z0-9]+$/.test(ext) ? ext : DEFAULT_EXTENSION;
}
